import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { glass } from '../glassStyles'

const PERIOD_OPTIONS = [
  { value: 7, label: '7日間' },
  { value: 14, label: '14日間' },
  { value: 30, label: '30日間' },
  { value: 90, label: '90日間' },
]

const FUNNEL_COLORS = [
  'rgba(99, 102, 241, 0.8)',
  'rgba(59, 130, 246, 0.8)',
  'rgba(245, 158, 11, 0.8)',
  'rgba(16, 185, 129, 0.8)',
]

export default function CustomerJourney() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()
  const [data, setData] = useState(null)
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(true)
  const [suggestions, setSuggestions] = useState('')
  const [sugLoading, setSugLoading] = useState(false)

  const fetchData = () => {
    setLoading(true)
    setData(null)
    fetch(`/api/sites/${siteId}/journey-analysis?days=${days}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false) })
      .catch(() => setLoading(false))
  }

  useEffect(() => { fetchData() }, [siteId, days])

  const fetchSuggestions = () => {
    if (!data) return
    setSugLoading(true)
    setSuggestions('')
    fetch(`/api/sites/${siteId}/journey-analysis/suggestions`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        funnel: data.funnel,
        top_paths: data.top_paths,
        exit_pages: data.exit_pages,
        total_sessions: data.total_sessions,
        conversion_rate: data.conversion_rate,
      }),
    })
      .then(r => r.json())
      .then(d => { setSuggestions(d.suggestions || ''); setSugLoading(false) })
      .catch(() => { setSuggestions('取得に失敗しました'); setSugLoading(false) })
  }

  const formatNum = (n) => n != null ? n.toLocaleString() : '0'

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>ジャーニー分析</h1>
          <p style={styles.subtitle}>行動データから購入導線・離脱ポイント・ファネルを分析</p>
        </div>
        <div style={styles.periodSelect}>
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setDays(opt.value)}
              style={{
                ...styles.periodBtn,
                ...(days === opt.value ? styles.periodBtnActive : {}),
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={styles.loadingWrap}>
          <div style={styles.spinner} />
        </div>
      ) : !data || data.total_sessions === 0 ? (
        <div style={{ ...glass.cardDark, padding: 40, textAlign: 'center' }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }}>対象期間の行動データがありません</p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={styles.kpiGrid}>
            {[
              { label: '総セッション数', value: formatNum(data.total_sessions), color: '#818cf8' },
              { label: 'ユニーク訪問者', value: formatNum(data.total_visitors), color: '#60a5fa' },
              { label: 'CVR', value: `${data.conversion_rate}%`, color: '#34d399' },
              { label: '平均アクション/セッション', value: data.avg_actions_per_session, color: '#fbbf24' },
            ].map((kpi, i) => (
              <div key={i} style={styles.kpiCard}>
                <div style={styles.kpiLabel}>{kpi.label}</div>
                <div style={{ ...styles.kpiValue, color: kpi.color }}>{kpi.value}</div>
              </div>
            ))}
          </div>

          {/* Funnel Analysis */}
          <div style={{ ...glass.cardDark, padding: 24, marginBottom: 20 }}>
            <h2 style={styles.sectionTitle}>ファネル分析</h2>
            <div style={styles.funnelWrap}>
              {data.funnel.map((step, i) => {
                const maxCount = Math.max(...data.funnel.map(f => f.count), 1)
                const width = Math.max(8, (step.count / maxCount) * 100)
                return (
                  <div key={step.step} style={styles.funnelStep}>
                    <div style={styles.funnelLabel}>
                      <span style={styles.funnelStepName}>{step.label}</span>
                      <span style={styles.funnelCount}>{formatNum(step.count)} ({step.rate}%)</span>
                    </div>
                    <div style={styles.funnelBarBg}>
                      <div
                        style={{
                          ...styles.funnelBar,
                          width: `${width}%`,
                          background: FUNNEL_COLORS[i] || FUNNEL_COLORS[0],
                        }}
                      />
                    </div>
                    {i < data.funnel.length - 1 && data.funnel[i + 1].count > 0 && (
                      <div style={styles.funnelDropoff}>
                        {step.count > 0
                          ? `${Math.round((1 - data.funnel[i + 1].count / step.count) * 100)}% 離脱`
                          : ''}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Two column: Top paths + Exit pages */}
          <div style={styles.twoCol}>
            {/* Top Paths */}
            <div style={{ ...glass.cardDark, padding: 24, flex: 1 }}>
              <h2 style={styles.sectionTitle}>購入導線 Top5</h2>
              {data.top_paths.length === 0 ? (
                <p style={styles.emptyText}>購入セッションがありません</p>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>アクションパス</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>件数</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>割合</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_paths.map((p, i) => (
                      <tr key={i}>
                        <td style={styles.td}>{i + 1}</td>
                        <td style={{ ...styles.td, maxWidth: 300, wordBreak: 'break-all' }}>
                          <div style={styles.pathWrap}>
                            {p.path.split(' → ').map((action, j, arr) => (
                              <React.Fragment key={j}>
                                <span style={styles.pathBadge(action)}>{action}</span>
                                {j < arr.length - 1 && <span style={styles.pathArrow}>→</span>}
                              </React.Fragment>
                            ))}
                          </div>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>{p.count}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>{p.rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Exit Pages */}
            <div style={{ ...glass.cardDark, padding: 24, flex: 1 }}>
              <h2 style={styles.sectionTitle}>離脱ページ Top10</h2>
              {data.exit_pages.length === 0 ? (
                <p style={styles.emptyText}>十分なデータがありません</p>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>ページURL</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>離脱数</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>閲覧数</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>離脱率</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.exit_pages.map((e, i) => (
                      <tr key={i}>
                        <td style={{ ...styles.td, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={e.page_url}>
                          {e.page_url}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>{e.exit_count}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>{e.total_views}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <span style={{
                            color: e.exit_rate > 70 ? '#f87171' : e.exit_rate > 50 ? '#fbbf24' : '#34d399',
                            fontWeight: 600,
                          }}>
                            {e.exit_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* AI Suggestions */}
          <div style={{ ...glass.cardDark, padding: 24, marginTop: 20 }}>
            <div style={styles.sugHeader}>
              <h2 style={styles.sectionTitle}>AI改善提案</h2>
              <button
                onClick={fetchSuggestions}
                disabled={sugLoading}
                style={{
                  ...glass.btn,
                  ...glass.btnPrimary,
                  opacity: sugLoading ? 0.6 : 1,
                }}
              >
                {sugLoading ? '分析中...' : '改善提案を生成'}
              </button>
            </div>
            {suggestions ? (
              <div style={styles.sugContent}>
                {suggestions.split('\n').map((line, i) => {
                  if (line.startsWith('■')) {
                    return <p key={i} style={styles.sugTitle}>{line}</p>
                  }
                  return <p key={i} style={styles.sugLine}>{line}</p>
                })}
              </div>
            ) : !sugLoading ? (
              <p style={styles.emptyText}>ボタンをクリックするとAIがジャーニーデータを分析し、改善提案を生成します</p>
            ) : (
              <div style={{ textAlign: 'center', padding: 30 }}>
                <div style={styles.spinner} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

const ACTION_COLORS = {
  view: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8' },
  view_product: { bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' },
  cart: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24' },
  order: { bg: 'rgba(16, 185, 129, 0.15)', color: '#34d399' },
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.9)',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.45)',
    marginTop: 4,
  },
  periodSelect: {
    display: 'flex',
    gap: 4,
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 10,
    padding: 3,
  },
  periodBtn: {
    padding: '6px 14px',
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  periodBtnActive: {
    background: 'rgba(99, 102, 241, 0.3)',
    color: '#c7d2fe',
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: 60,
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: '#818cf8',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
    marginBottom: 20,
  },
  kpiCard: {
    background: 'rgba(15, 23, 42, 0.75)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 14,
    padding: '18px 20px',
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.45)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 6,
  },
  kpiValue: {
    fontSize: 28,
    fontWeight: 700,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.9)',
    margin: '0 0 16px 0',
  },
  funnelWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  funnelStep: {
    position: 'relative',
  },
  funnelLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  funnelStepName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.8)',
  },
  funnelCount: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
  },
  funnelBarBg: {
    width: '100%',
    height: 28,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 6,
    overflow: 'hidden',
  },
  funnelBar: {
    height: '100%',
    borderRadius: 6,
    transition: 'width 0.6s ease-out',
  },
  funnelDropoff: {
    textAlign: 'right',
    fontSize: 11,
    color: '#f87171',
    marginTop: 2,
    marginBottom: 4,
  },
  twoCol: {
    display: 'flex',
    gap: 20,
    flexWrap: 'wrap',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  td: {
    padding: '10px 10px',
    fontSize: 13,
    color: 'rgba(255,255,255,0.75)',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  pathWrap: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 3,
  },
  pathBadge: (action) => {
    const c = ACTION_COLORS[action] || { bg: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }
    return {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      background: c.bg,
      color: c.color,
    }
  },
  pathArrow: {
    color: 'rgba(255,255,255,0.25)',
    fontSize: 10,
    margin: '0 1px',
  },
  emptyText: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 13,
    textAlign: 'center',
    padding: 20,
  },
  sugHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sugContent: {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 10,
    padding: 20,
  },
  sugTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#c7d2fe',
    margin: '16px 0 6px 0',
  },
  sugLine: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.7)',
    margin: '4px 0',
    lineHeight: 1.6,
  },
}
